import {HTMLAttributes, useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {PWD_STRENGTH_LIST, PWD_STRENGTH_STATE, PWD_STRENGTH_STATE_COLOR} from '@/constants';
import {PwdStrengthConditionType} from '@/enums';
import {Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {getPasswordStrength, getPwdStrengthLevel, hasInvalidCharacterInPwd} from '@/utils';
import {Button} from '../Button';
import {CheckIconComponent} from '../Icons';
import {TextField} from '../TextField';
import {Typography, typographyStyle} from '../Typography';
import {InputContainer, TextFieldLabel} from './StyledComponents';

interface Inputs {
  password: string;
  passwordCheck: string;
}

interface Values {
  password?: string;
  passwordCheck?: string;
}

interface IProps extends HTMLAttributes<HTMLFormElement> {
  defaultValue?: string;
  loading?: boolean;
  onChanged(data: Pick<Values, 'password'>): void;
  onComplete(): void;
}

export const SetPasswordStep = ({
  defaultValue,
  loading,
  onChanged,
  onComplete,
  ...props
}: IProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: {errors, isValid},
  } = useForm<Inputs>({mode: 'all'});

  const [values, setValues] = useState<Values>({
    password: defaultValue,
    passwordCheck: defaultValue,
  });

  const strengthCondition = getPasswordStrength(values?.password);
  const strengthLevel = hasInvalidCharacterInPwd(values?.password)
    ? 0
    : getPwdStrengthLevel(strengthCondition);
  const strengthState =
    !!values?.password?.length && PWD_STRENGTH_STATE[strengthLevel];

  const passwordStrengthValidate = (value: string) => {
    if (hasInvalidCharacterInPwd(value)) {
      return '유효하지 않은 문자가 포함되어 있습니다.';
    }

    if (!getPwdStrengthLevel(getPasswordStrength(value))) {
      return PWD_STRENGTH_STATE[0];
    }
  };

  const passwordCheckValidate = (value: string, formValues: Inputs) => {
    if (formValues.password !== value) {
      return '비밀번호가 일치하지 않습니다.';
    }
  };

  const onSubmit: SubmitHandler<Inputs> = () => {
    onComplete();
  };

  useEffect(() => {
    setFocus('password');
  }, [setFocus]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value);
      onChanged({password: value.password});
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const pwdError = errors.password?.message;
  const passCharCondition =
    strengthCondition.filter((i) => PWD_STRENGTH_LIST.some((j) => j.type === i))
      ?.length > 1;

  return (
    <Root as="form" {...props} onSubmit={handleSubmit(onSubmit)}>
      <div>
        <TextFieldLabel error={!!pwdError?.length}>비밀번호</TextFieldLabel>
        <FieldWrapper>
          <TextField
            {...register('password', {
              validate: passwordStrengthValidate,
              required: '비밀번호를 입력해 주세요.',
            })}
            fullWidth
            hasMaskButton
            variant="xlarge"
            placeholder="8자 이상, 문자/숫자/기호 사용"
            type="password"
            autoComplete="new-password"
            maxLength={100}
            value={values.password}
            error={pwdError === PWD_STRENGTH_STATE[0] ? true : pwdError}
          />
          <StrengthLevel
            style={{color: PWD_STRENGTH_STATE_COLOR[strengthLevel]}}
          >
            {strengthState}
          </StrengthLevel>
        </FieldWrapper>
        <PaddingBox top={8}>
          <ConditionCheckItem
            passed={strengthCondition.includes(
              PwdStrengthConditionType.minLength
            )}
            label="8자 이상"
          />
          <PaddingBox top={4}>
            <ConditionCheckItem
              passed={passCharCondition}
              label="영소문자/영대문자/숫자/기호 중 최소 2가지 포함"
            />
          </PaddingBox>
        </PaddingBox>
      </div>
      <div>
        <TextFieldLabel error={!!errors.passwordCheck?.message?.length}>
          비밀번호 확인
        </TextFieldLabel>
        <TextField
          {...register('passwordCheck', {
            required: '입력하신 비밀번호를 다시 한번 입력해 주세요.',
            validate: passwordCheckValidate,
          })}
          fullWidth
          hasMaskButton
          variant="xlarge"
          placeholder="비밀번호 확인"
          type="password"
          autoComplete="new-password"
          maxLength={100}
          value={values.passwordCheck}
          error={errors.passwordCheck?.message}
        />
      </div>
      <FlexFullGrow />
      <Button
        type="submit"
        disabled={!isValid || loading}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
      >
        {loading ? '변경 중...' : '다음'}
      </Button>
    </Root>
  );
};

const ConditionCheckItem = ({
  passed,
  label,
}: {
  passed?: boolean;
  label: string;
}) => {
  return (
    <Flexbox>
      <CheckIconComponent
        style={{paddingRight: 2}}
        color={passed ? palette.green400 : palette.gray300}
      />
      <Typography variant="medium12" color={palette.gray600}>
        {label}
      </Typography>
    </Flexbox>
  );
};

const FieldWrapper = styled.div`
  position: relative;
`;

const StrengthLevel = styled.div`
  ${{...typographyStyle.medium14}}
  position: absolute;
  right: 40px;
  top: 15px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const Root = styled(InputContainer)`
  gap: 16px;
`;
